import { useState } from 'react';
import { Handshake, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { typedApi } from '../api/typed';
import { useResource, invalidate } from '../hooks/useResource';
import { logger } from '../utils/logger';

/**
 * Dropdown to pick an existing commitment to link an item to after creation.
 * Shows only non-DONE commitments. Includes a "+ New" inline-stub footer so a
 * commitment can be created name-only on the spot (gets an "incomplete" badge
 * until filled out). The footer renders even when zero commitments exist, so
 * the very first bundling is possible (no chicken-and-egg).
 */
export function CommitmentPicker({ value, onChange }) {
  const { data: commitments } = useResource(
    'commitment-picker',
    async ({ signal }) => {
      const data = await typedApi.get('/api/commitments', { signal });
      return (data.commitments || []).filter(c => c.status !== 'DONE');
    },
    { initialData: [] },
  );
  const [creating, setCreating] = useState(false);
  const [stubName, setStubName] = useState('');
  const [saving, setSaving] = useState(false);

  const createStub = async () => {
    const title = stubName.trim();
    if (!title || saving) return;
    setSaving(true);
    try {
      const created = await typedApi.post('/api/commitments', { title, status: 'OPEN', incomplete: true });
      invalidate('commitment-picker'); // refresh every mounted picker so the stub appears
      if (created?.id) onChange(created.id); // auto-select the new stub
      setStubName('');
      setCreating(false);
    } catch (err) {
      logger.error('Failed to create commitment stub:', err);
      toast.error('Could not create commitment');
    } finally {
      setSaving(false);
    }
  };

  const cancelCreate = () => { setCreating(false); setStubName(''); };
  const list = commitments || [];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Handshake className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
        {list.length > 0 ? (
          <select
            data-testid="commitment-picker"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            className="flex-1 px-3 py-2 rounded-[10px] text-[11px] outline-none"
            style={{ border: '1px solid var(--pd-border)', color: value ? 'var(--pd-text)' : 'var(--pd-muted)', background: 'var(--pd-panel-solid)' }}
          >
            <option value="">Link to commitment...</option>
            {list.map(c => (
              <option key={c.id} value={c.id}>
                {c.title}{c.incomplete ? ' · (incomplete)' : ''}{c.stakeholder ? ` · ${c.stakeholder}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <span className="flex-1 text-[11px]" style={{ color: 'var(--pd-muted)' }}>No commitments yet</span>
        )}
        {!creating && (
          <button
            type="button"
            data-testid="commitment-picker-new-toggle"
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 px-2.5 py-2 rounded-[10px] text-[11px] font-semibold flex-shrink-0 transition-colors"
            style={{ border: '1px solid var(--pd-border)', color: 'var(--pd-focus)' }}
            title="Create a new commitment"
          >
            <Plus className="w-3 h-3" />New
          </button>
        )}
      </div>

      {creating && (
        <div data-testid="commitment-picker-stub-form" className="flex items-center gap-2">
          <input
            data-testid="commitment-picker-stub-name"
            autoFocus
            value={stubName}
            onChange={(e) => setStubName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); createStub(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelCreate(); }
            }}
            placeholder="New commitment name..."
            className="flex-1 px-3 py-2 rounded-[10px] text-[11px] outline-none"
            style={{ border: '1px solid var(--pd-focus)', color: 'var(--pd-text)', background: 'var(--pd-panel-solid)' }}
          />
          <button
            type="button"
            data-testid="commitment-picker-stub-create"
            onClick={createStub}
            disabled={saving || !stubName.trim()}
            className="flex items-center gap-1 px-2.5 py-2 rounded-[10px] text-[11px] font-semibold text-white flex-shrink-0"
            style={{ background: 'var(--pd-focus)', opacity: (saving || !stubName.trim()) ? 0.5 : 1 }}
          >
            <Check className="w-3 h-3" />{saving ? '...' : 'Create'}
          </button>
          <button
            type="button"
            data-testid="commitment-picker-stub-cancel"
            onClick={cancelCreate}
            className="p-2 rounded-[10px] flex-shrink-0"
            style={{ color: 'var(--pd-muted)' }}
            aria-label="Cancel new commitment"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
